/**
 * "Ver na fonte" (E139.F3, ADR-0095 §2.7): a página do arquivo recebido, em imagem, pela rota que
 * já existe e que confere a custódia antes de entregar. O arquivo guardado que não confere com o
 * recebido não é exibido, e a tela diz por quê.
 *
 * E184.F4.T01 (GUS-145 item 3): ao lado da imagem, "Abrir o original" abre o arquivo numa aba nova,
 * para selecionar e copiar o texto do PDF.
 *
 * E201.F2 (GUS-47, ADR-0144 §8): dentro da mesa única, "Ver na fonte" abre o arquivo e a página
 * citados no painel do documento original, ao lado do exame, e não o diálogo. Fora da mesa (sem o
 * painel) o diálogo segue.
 */
import axios, { type AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { lerImagemDaPagina, type PaginaNaFonte } from '@/lib/ficha_da_leitura';
import { usePainelDoDocumento } from '@/lib/painelDoDocumento';

import { AbrirOriginal } from './AbrirOriginal';
import { preencher, t } from './textos';

const HTTP_CONFLICT = 409;

type Imagem =
  | { estado: 'carregando' }
  | { estado: 'pronta'; endereco: string }
  | { estado: 'erro'; divergente: boolean };

function useImagem(client: AxiosInstance, alvo: PaginaNaFonte | null): Imagem {
  const [imagem, setImagem] = useState<Imagem>({ estado: 'carregando' });
  useEffect(() => {
    if (alvo === null) return undefined;
    let vivo = true;
    let endereco: string | null = null;
    setImagem({ estado: 'carregando' });
    void lerImagemDaPagina(client, alvo.documentoId, alvo.pagina)
      .then((url) => {
        endereco = url;
        if (vivo) setImagem({ estado: 'pronta', endereco: url });
        else URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => {
        const divergente = axios.isAxiosError(err) && err.response?.status === HTTP_CONFLICT;
        if (vivo) setImagem({ estado: 'erro', divergente });
      });
    return () => {
      vivo = false;
      if (endereco !== null) URL.revokeObjectURL(endereco);
    };
  }, [client, alvo]);
  return imagem;
}

export interface PaginaDaFonteProps {
  client: AxiosInstance;
  caseId: string;
  alvo: PaginaNaFonte | null;
  onFechar: () => void;
}

export function PaginaDaFonte({
  client,
  caseId,
  alvo,
  onFechar,
}: PaginaDaFonteProps): JSX.Element | null {
  const painel = usePainelDoDocumento();
  useEffect(() => {
    if (painel === null || alvo === null) return;
    painel.abrir({ documentoId: alvo.documentoId, pagina: alvo.pagina });
    onFechar();
  }, [painel, alvo, onFechar]);
  // No painel a página não vira imagem: o painel mostra o próprio original.
  const imagem = useImagem(client, painel === null ? alvo : null);
  if (painel !== null) return null;
  const titulo =
    alvo === null ? '' : preencher(t.verPagina, { pagina: alvo.pagina, documento: alvo.titulo });
  return (
    <Dialog open={alvo !== null} onOpenChange={(aberto) => (aberto ? undefined : onFechar())}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-auto" data-testid="pagina-da-fonte">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{t.ressalva}</DialogDescription>
        </DialogHeader>
        {alvo !== null ? (
          <AbrirOriginal
            client={client}
            caseId={caseId}
            documentoId={alvo.documentoId}
            nome={alvo.titulo}
          />
        ) : null}
        {imagem.estado === 'carregando' ? (
          <p className="text-body-sm text-fg-muted">{t.pagina.carregando}</p>
        ) : null}
        {imagem.estado === 'erro' ? (
          <p
            role="alert"
            className="text-body-sm text-danger-700"
            data-testid="pagina-da-fonte-erro"
          >
            {imagem.divergente ? t.pagina.divergente : t.pagina.erro}
          </p>
        ) : null}
        {imagem.estado === 'pronta' && alvo !== null ? (
          <img
            src={imagem.endereco}
            alt={preencher(t.pagina.alt, { pagina: alvo.pagina, documento: alvo.titulo })}
            className="w-full border border-border"
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
